import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { suggestionMenuTookEsc } from '@shared/lib';
import * as S from './styles';

export type GhostInputSize = 'medium' | 'small';

export interface GhostInputProps {
  /**
   * The editor: anything that renders a `contenteditable`. On every surface today it is
   * the collaborative Tiptap editor, which a `shared` module cannot import, so the
   * surface passes it in.
   */
  children: ReactNode;
  /** Figma `size`: medium is 8/12 with radius 8 (the default), small is 4/8 with radius 4. */
  size?: GhostInputSize;
  /** Not editable now: no hover fill, no typing state, a plain cursor. */
  readOnly?: boolean;
  /** Why it is read-only, shown on hover. A frozen field with no reason reads as broken. */
  readOnlyHint?: string;
  /** Fill the parent's height, with the whole area clickable (Work performed). */
  grow?: boolean;
  /**
   * Line the text up with the content around the field: the field reaches into the
   * parent's gutter by its padding, so the hover fill and the typing ring keep their
   * inset (Work performed under its label).
   */
  bleed?: boolean;
  /** Focus entered the field, so editing it became the current action (P-23). */
  onActivate?: () => void;
  className?: string;
  'data-testid'?: string;
}

const EDITABLE_SELECTOR = '[contenteditable="true"]';

const findEditable = (root: HTMLElement) => root.querySelector<HTMLElement>(EDITABLE_SELECTOR);

/** Focuses the editable with the caret after its last character. */
const focusAtEnd = (editable: HTMLElement) => {
  editable.focus();
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(editable);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

/**
 * One text field for every documentation surface — Work performed, a legend entry's
 * meaning, a note's body — drawn as Figma `ghost-input` and behaving the same wherever
 * it is used (P-22).
 *
 * - The whole field is the target. A press on its padding puts the caret at the end of
 *   the text, so the field has no dead edge around the editable.
 * - A click inside an editable field stays in it: a host row must not read it as a click
 *   on the row. A read-only field lets the click through, so the row still answers it.
 * - Cmd/Ctrl+Enter ends the edit (FEAT-07 §4.4.3). It is caught before the editor, whose
 *   own binding for it is a line break. The editors save as they are typed in, so ending
 *   the edit only takes the focus away.
 * - Esc ends the edit too, in one press (P-23): while the field has the focus, editing it
 *   is the current action. An Esc that closed the editor's @ or / menu is the menu's,
 *   and a host with its own key map (the mark popover) catches Esc before the field.
 */
export default function GhostInput({
  children,
  size = 'medium',
  readOnly = false,
  readOnlyHint,
  grow = false,
  bleed = false,
  onActivate,
  className,
  'data-testid': dataTestId,
}: GhostInputProps) {
  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (readOnly || event.button !== 0) return;
    const editable = findEditable(event.currentTarget);
    if (!editable || editable.contains(event.target as Node)) return;
    // Without this the press would blur the editor and focus nothing.
    event.preventDefault();
    focusAtEnd(editable);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!readOnly) event.stopPropagation();
  };

  const endEdit = (editable: HTMLElement) => {
    editable.blur();
    window.getSelection()?.removeAllRanges();
  };

  /** Esc, after the editor: its suggestion menu marks the Esc that closed it. */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // 229: Safari ends an IME composition before the Esc that cancels it arrives.
    if (readOnly || event.key !== 'Escape' || event.nativeEvent.isComposing) return;
    if (event.keyCode === 229 || suggestionMenuTookEsc(event.nativeEvent)) return;
    const editable = findEditable(event.currentTarget);
    if (!editable || !editable.contains(event.target as Node)) return;
    endEdit(editable);
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly || event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
    const editable = findEditable(event.currentTarget);
    if (!editable || !editable.contains(event.target as Node)) return;
    event.preventDefault();
    event.stopPropagation();
    endEdit(editable);
  };

  return (
    <S.Root
      size={size}
      readOnly={readOnly}
      grow={grow}
      bleed={bleed}
      className={className}
      title={readOnly ? readOnlyHint : undefined}
      data-readonly={readOnly ? 'true' : 'false'}
      data-testid={dataTestId}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyDownCapture}
      onFocus={onActivate ? () => onActivate() : undefined}
    >
      {children}
    </S.Root>
  );
}
